import { HTMLProps, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { When } from 'react-if';
import { useEffectOnce } from 'react-use';
import { Menu } from '@headlessui/react';
import cns from 'classnames';
import { ParseKeys } from 'i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCopySpace, useDeleteSpaceVisitHistory } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { BoltFillIcon } from '@zep/icons';
import { useViewportDevice } from '@zep/react';
import { Stack } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { getResizedImage } from '@zep/utils';
import { SpaceUserRole, SpaceUserRoleName } from '../../../../consts';
import { Space } from '../../../../types';
import S from './NewSpaceCard.module.scss';
type NewSpaceCardProps = {
  space: Space;
};
const WHITE_DOMAINS = ['zep.us'];
export const NewSpaceCard = ({
  space
}: NewSpaceCardProps) => {
  const {
    t
  } = useTranslation(['home'], {
    useSuspense: true
  });
  const router = useRouter();
  const {
    user
  } = useAuth();
  const {
    isPortableDevice,
    isMobile
  } = useViewportDevice();
  const baseURL = process.env.NEXT_PUBLIC_BASE_URL || '';
  const deleteHistoryMutation = useDeleteSpaceVisitHistory();
  const copySpaceMutation = useCopySpace();
  const formattedPlanNames = space.spacePlanType.toUpperCase();
  const planColor = () => {
    switch (space.spacePlanType) {
      case 'BASIC':
        return Colors.MintDarkHover;
      case 'PRO':
        return Colors.PrimaryDefault;
      case 'ENTERPRISE':
        return Colors.Gray600;
    }
  };
  const userDomain = useMemo(() => {
    return user?.snsId?.includes('@') ? user.snsId.split('@')[1] : '';
  }, [user?.snsId]);
  const filteredMenus = useMemo(() => {
    const menus = [{
      id: '스페이스방문기록삭제',
      labelKey: 'home:space_delete_visited_spacehistory',
      isVisible: space.myRole <= SpaceUserRole.NORMAL,
      imgSrc: '/images/light/spaceMe/trash_red.svg',
      alt: 'trash',
      onClick: () => {
        deleteHistoryMutation.mutate(space.hashId);
      }
    }, {
      id: '스페이스복사',
      labelKey: 'space_copy_as_admin_above',
      isVisible: space.myRole >= SpaceUserRole.ADMIN || !!user?.gmLevel && user?.gmLevel >= 1,
      imgSrc: '/images/light/spaceMe/copy_spaces_black.svg',
      alt: 'copy',
      onClick: () => {
        copySpaceMutation.mutate(space.hashId);
      }
    }, {
      id: '맵에디터',
      labelKey: 'home:space_map_editor',
      isVisible: space.myRole >= SpaceUserRole.CAN_EDIT && !isPortableDevice,
      imgSrc: '/images/light/spaceMe/hammer.svg',
      alt: 'hammer',
      onClick: () => {
        router.push(`/edit/${space.hashId}/${space.entryMapHashId}`, undefined, {
          locale: false
        });
      }
    }, {
      id: '스페이스해시',
      labelKey: 'copy hashId',
      isVisible: WHITE_DOMAINS.includes(userDomain),
      imgSrc: '/images/light/spaceMe/copy_spaces_black.svg',
      alt: 'hash',
      onClick: () => {
        navigator.clipboard.writeText(space.hashId);
        toast.success('스페이스 해시 ID를 복사했습니다.');
      }
    }];
    return menus.filter(menu => menu.isVisible);
  }, [space.myRole, space.hashId, space.entryMapHashId, user?.gmLevel, userDomain, isPortableDevice, deleteHistoryMutation, copySpaceMutation, router]);

  /**
   * 임시적으로 현재 api 응답에 v1이미지 경로가 응답으로 오는 경우가 있어서 해당 이미지들만 예외처리함
   * TODO: 추후 썸네일 템플릿들 전부 s3로 관리하기 시작하면 제거
   */
  useEffectOnce(() => {
    if (space.thumbnailUrl.charAt(0) === '/') {
      space.thumbnailUrl = baseURL + space.thumbnailUrl;
    }
  });
  return <div className={S.space_card_wrapper} data-sentry-component="NewSpaceCard" data-sentry-source-file="NewSpaceCard.tsx">
      <Link href={`/play/${space.entryMapHashId}`} passHref legacyBehavior prefetch={false} data-sentry-element="Link" data-sentry-source-file="NewSpaceCard.tsx">
        <a>
          <div className={S.thumbnail_wrapper}>
            <img className={S.thumbnail} src={getResizedImage(space.thumbnailUrl, 600) ?? '/images/light/spaceMe/no_thumbnail_image.png'} alt="Thumbnail" onError={e => e.currentTarget.src = '/images/light/spaceMe/no_thumbnail_image.png'} />
            {space.myRole >= SpaceUserRole.STAFF && <div className={cns(S.mark_wrapper, space.myRole === SpaceUserRole.STAFF && S.mark_wrapper_staff)}>
                <img src="/images/light/spaceMe/crown_white.svg" alt="crown" />
                <span className={S.mark_label}>
                  {SpaceUserRoleName[space.myRole]}
                </span>
              </div>}
            {space.onlinePlayers > 0 && <div className={S.people_count}>
                <div className={S.green_dot}></div>
                <span className={S.count_label}>{space.onlinePlayers}</span>
              </div>}
          </div>
        </a>
      </Link>
      <div className={S.name_wrapper}>
        <Stack gap={isMobile ? 2 : 4} data-sentry-element="Stack" data-sentry-source-file="NewSpaceCard.tsx">
          <span className={S.space_name}>{space.name}</span>
          <When condition={space.spacePlanType !== 'FREE'} data-sentry-element="When" data-sentry-source-file="NewSpaceCard.tsx">
            <span className={S.space_plan} data-plan={space.spacePlanType}>
              <BoltFillIcon width={12} height={12} fill={planColor()} data-sentry-element="BoltFillIcon" data-sentry-source-file="NewSpaceCard.tsx" />
              <span>{formattedPlanNames}</span>
            </span>
          </When>
        </Stack>
        {filteredMenus.length > 0 && <Menu as="div" className={S.dropdown}>
            <Menu.Button className={S.dropdown_toggle}>
              <img src="/images/light/common/dots_black.svg" alt="더보기" />
            </Menu.Button>
            <Menu.Items className={S.dropdown_list}>
              {filteredMenus.map(menu => {
            const {
              labelKey,
              imgSrc,
              onClick,
              id,
              alt,
              isVisible
            } = menu;
            return <When condition={isVisible} key={id}>
                    <MenuItemButton onClick={onClick}>
                      <img src={imgSrc} alt={alt} />
                      {t(labelKey as ParseKeys<'home'>)}
                    </MenuItemButton>
                  </When>;
          })}
            </Menu.Items>
          </Menu>}
      </div>
    </div>;
};
const MenuItemButton = (props: HTMLProps<HTMLButtonElement>) => {
  const {
    className,
    children,
    type: _,
    ...rest
  } = props;
  return <Menu.Item data-sentry-element="unknown" data-sentry-component="MenuItemButton" data-sentry-source-file="NewSpaceCard.tsx">
      {({
      active
    }) => <button className={cns(S.dropdown_item, {
      [S.is_active]: active
    }, className)} {...rest}>
          {children}
        </button>}
    </Menu.Item>;
};